import { OverlayContainer } from '@angular/cdk/overlay';
import { Component, effect, Inject, inject, NgZone } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import {
  Router,
  RouterOutlet,
  ActivatedRoute,
  NavigationEnd,
} from '@angular/router';
import { takeUntil } from 'rxjs';
import { CUSTOM_ICONS } from './common/constants/icons';
import { SubscriptionManager } from '../utils/subscription-manager';
import { AuthApiService } from './core/auth/auth-api.service';
import { OnboardingLayout } from './layouts/onboarding-layout/onboarding-layout.component';
import { ShopLayout } from './layouts/shop-layout/shop-layout.component';
import { LoadingService } from './services/shared/loader/loading.service';
import { MatDialog } from '@angular/material/dialog';
import { openOfflineModal } from './shared/components/modal/offline-dialog/offline-dialog.component';
import { DebugLoggerComponent } from './shared/components/debug-logger/debug-logger.component';
import { DebugLogger } from './shared/components/debug-logger/debug-logger.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, ShopLayout, OnboardingLayout, DebugLoggerComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
  host: {
    '[class.cursor-wait]': 'loading()',
  },
})
export class AppComponent extends SubscriptionManager {
  title = 'admin-panel';
  pageLayout: string = 'default';

  loading = this.loadingService.loading;

  #dialog = inject(MatDialog);
  #ngZone = inject(NgZone);
  #debugLogger = inject(DebugLogger);

  constructor(
    private authService: AuthApiService,
    private loadingService: LoadingService,
    private router: Router,
    private overlayContainer: OverlayContainer,
    public activatedRoute: ActivatedRoute,

    @Inject(MatIconRegistry) private readonly iconRegistry: MatIconRegistry,
    @Inject(DomSanitizer) private readonly sanitizer: DomSanitizer,
  ) {
    super();

    effect(() => {
      this.toggleLoadingCursor(this.loading());
    });

    this.authService.currentUser$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data) => {
        this.userChanged();
      });

    this.registerCustomSvgIcons();
    this.loadTawkTo();
    this.setupOfflineListener();

    this.#debugLogger.log('AppComponent initialized');

    const viewport = window.visualViewport;

    viewport?.addEventListener('resize', () => {
      this.#debugLogger.log('viewport.resize', {
        width: viewport.width,
        height: viewport.height,
        scale: viewport.scale,
        offsetTop: viewport.offsetTop,
        offsetLeft: viewport.offsetLeft,

        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,

        screenWidth: screen.width,
        screenHeight: screen.height,

        activeElement: document.activeElement?.tagName,

        keyboardLikelyOpen:
          viewport.height < Math.max(window.innerHeight, screen.height) * 0.8,
      });
    });

    document.addEventListener('fullscreenchange', () => {
      this.#debugLogger.log('fullscreen.change', {
        fullscreen: !!document.fullscreenElement,
        fullscreenElement: document.fullscreenElement?.tagName ?? null,
        viewportHeight: window.visualViewport?.height,
        innerHeight: window.innerHeight,
      });
    });

    document.addEventListener('visibilitychange', () => {
      this.#debugLogger.log('visibility.change', {
        visibilityState: document.visibilityState,
        hidden: document.hidden,
      });
    });

    // document.addEventListener(
    //   'focusin',
    //   (e) => {
    //     this.#debugLogger.log('focus.in', {
    //       element: (e.target as HTMLElement).tagName,
    //     });
    //   },
    //   true,
    // );

    // document.addEventListener(
    //   'focusout',
    //   (e) => {
    //     this.#debugLogger.log('focus.out', {
    //       element: (e.target as HTMLElement).tagName,
    //     });
    //   },
    //   true,
    // );
  }

  ngOnInit() {
    // 1. Hide widget whenever ANY dialog opens
    this.#dialog.afterOpened.subscribe(() => {
      if (window.Tawk_API) {
        window.Tawk_API.hideWidget();
      }
    });

    // 2. Show widget only when ALL dialogs are closed
    this.#dialog.afterAllClosed.subscribe(() => {
      if (window.Tawk_API) {
        window.Tawk_API.showWidget();
      }
    });
  }

  ngAfterViewInit() {
    this.initTawk();
  }

  /**
   * Listen for browser offline event and show dialog when user goes offline
   */
  private setupOfflineListener(): void {
    window.addEventListener('offline', () => {
      this.#ngZone.run(() => {
        this.handleOffline();
      });
    });

    window.addEventListener('online', () => {
      this.#ngZone.run(() => {
        const existingDialog = this.#dialog.getDialogById('offline-modal');
        if (existingDialog) {
          existingDialog.close();
        }
      });
    });
  }

  private handleOffline(): void {
    if (navigator.onLine) {
      return;
    }

    if (!this.authService.currentUser()) {
      return;
    }

    const currentUrl = this.router.url;

    if (currentUrl.includes('/auth/') || currentUrl === '/') {
      return;
    }

    if (
      currentUrl.includes('/registers/point-of-sales') ||
      currentUrl.includes('/registers/open-close-register')
    ) {
      return;
    }

    const existingDialog = this.#dialog.getDialogById('offline-modal');
    if (!existingDialog) {
      openOfflineModal(this.#dialog);
    }
  }

  toggleLoadingCursor(loading: boolean) {
    // Add/remove cursor-wait to Angular Material's CDK overlay container
    // -- container inside which all overlays will render (e.g. dialog)

    const overlayContainerClassList =
      this.overlayContainer.getContainerElement().classList;

    if (loading) {
      overlayContainerClassList.add('cursor-wait');
    } else {
      overlayContainerClassList.remove('cursor-wait');
    }
  }

  userChanged() {
    this.router.events.pipe(takeUntil(this.unsubscribe$)).subscribe((evt) => {
      if (evt instanceof NavigationEnd) {
        const currentRoute = this.activatedRoute?.snapshot?.firstChild;
        this.pageLayout = currentRoute?.data['layout'] ?? 'default';
      }
    });
  }

  registerCustomSvgIcons() {
    for (const icon of CUSTOM_ICONS) {
      this.iconRegistry.addSvgIcon(
        icon,
        this.sanitizer.bypassSecurityTrustResourceUrl(
          `./assets/icons/${icon}.svg`,
        ),
      );
    }

    this.preloadCriticalIcons();
    this.preloadCriticalImages();
  }

  /**
   * Preload critical SVG icons that are needed for offline mode.
   * MatIconRegistry loads icons lazily, so we need to trigger the fetch
   * while online to ensure they're cached by the service worker.
   */
  private preloadCriticalIcons(): void {
    const criticalIcons = [
      'close',
      'error',
      'warn',
      'check',
      'check-circle',
      'info',
      'plus',
      'minus',
      'trash',
      'pencil',
      'printer',
      'tag',
      'percent',
      'ticket-check',
      'ticket-x',
      'banknote-arrow-up',
      'banknote-arrow-down',
      'wallet',
      'loading',
    ];

    for (const icon of criticalIcons) {
      this.iconRegistry.getNamedSvgIcon(icon).subscribe({ error: () => {} });
    }
  }

  /**
   * Preload critical images that are needed for offline mode.
   * This fetches images in the background so they're cached by the service worker.
   */
  private preloadCriticalImages(): void {
    const criticalImages = [
      '/assets/images/logo-primary.svg',
      '/assets/images/logo-icon.svg',
      '/assets/images/transaction-complete.svg',
      'https://cdn-icons-png.flaticon.com/512/11329/11329060.png',
    ];

    for (const src of criticalImages) {
      const img = new Image();
      img.src = src;
    }
  }

  private loadTawkTo() {
    if (document.getElementById('tawkToScript')) {
      return; // Prevent adding it multiple times
    }

    const s1 = document.createElement('script');
    s1.id = 'tawkToScript';
    s1.async = true;
    s1.src = 'https://embed.tawk.to/67e7e4e42966e4190c90752f/1ingtc803';
    s1.setAttribute('crossorigin', '*');

    const s0 = document.getElementsByTagName('script')[0];
    s0.parentNode?.insertBefore(s1, s0);
  }

  private initTawk() {
    // Ensure Tawk widget is minimized when it loads.
    // This is added because the Tawk chat preview (proactive message bubbles)
    // can overlap modals, dropdowns, and other panels, blocking user interaction.
    // By minimizing, we hide the preview while keeping the chat launcher visible.
    // ⚠️ Optionally, we may scope this behavior to specific pages instead of all pages.
    window.Tawk_API = window.Tawk_API || {};
    (window as any).Tawk_API.customStyle = {
      visibility: {
        desktop: {
          position: 'br',
          xOffset: 20,
          yOffset: 40,
        },
        mobile: {
          position: 'br',
          xOffset: 20,
          yOffset: 40,
        },
      },
    };
    (window as any).Tawk_API.onLoad = () => {
      console.log('Tawk widget initialized, minimizing…');
      (window as any).Tawk_API.minimize();
    };
  }
}
